import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaFlag, FaArrowLeft, FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

type ReportReason =
  | 'hate'
  | 'harassment'
  | 'sexual'
  | 'self_harm'
  | 'violence'
  | 'copyright'
  | 'privacy'
  | 'other';

const reasonLabels: Record<ReportReason, string> = {
  violence: 'Violence',
  hate: 'Haine/Discrimination',
  sexual: 'Contenu sexuel',
  harassment: 'Harcèlement',
  privacy: 'Atteinte à la vie privée',
  copyright: "Droits d'auteur",
  self_harm: 'Automutilation',
  other: 'Autre',
};

const reasonDescriptions: Record<ReportReason, string> = {
  violence: 'Contenu violent, menaces ou incitation à la violence',
  hate: 'Discours de haine, discrimination ou intolérance',
  sexual: 'Contenu sexuellement explicite ou inapproprié',
  harassment: 'Intimidation, harcèlement ou comportement abusif',
  privacy: 'Partage d\'informations personnelles sans consentement',
  copyright: 'Violation des droits d\'auteur ou propriété intellectuelle',
  self_harm: 'Contenu encourageant l\'automutilation ou le suicide',
  other: 'Autre violation des règles de la communauté',
};

export default function ReportPage() {
  const [selectedReason, setSelectedReason] = useState<ReportReason | ''>('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();

  // Get content data from navigation state
  const { contentId, contentPreview, returnUrl } = location.state || {};

  useEffect(() => {
    // Redirect if no content data provided
    if (!contentId || !contentPreview) {
      toast.error('Données de signalement manquantes');
      navigate('/dashboard');
    }
  }, [contentId, contentPreview, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedReason || !user) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('report_ai_content_reports')
        .insert({
          user_id: user.id,
          content_id: contentId,
          content_preview: contentPreview.substring(0, 500),
          reason: selectedReason,
          details: details.trim() || null,
          status: 'open'
        });

      if (error) throw error;

      setSubmitted(true);
      toast.success('Signalement envoyé avec succès !');
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate(returnUrl || '/dashboard');
      }, 3000);
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast.error('Erreur lors de l\'envoi du signalement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(returnUrl || '/dashboard');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-green-500 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheck className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Signalement Envoyé</h1>
              <p className="text-green-100">
                Merci d'avoir signalé ce contenu. Notre équipe va l'examiner.
              </p>
            </div>
            
            <div className="p-6 text-center">
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 mb-2">Prochaines étapes</h3>
                  <ul className="text-sm text-green-700 space-y-1 text-left">
                    <li>• Notre équipe de modération va examiner votre signalement</li>
                    <li>• Vous recevrez une notification si des mesures sont prises</li>
                    <li>• Le contenu peut être masqué ou supprimé si nécessaire</li>
                  </ul>
                </div>
                
                <p className="text-gray-600 text-sm">
                  Vous serez redirigé automatiquement dans quelques secondes...
                </p>
                
                <button
                  onClick={handleGoBack}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
                >
                  Retourner maintenant
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-red-500 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FaFlag className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Signaler du Contenu</h1>
                  <p className="text-red-100">Aidez-nous à maintenir une communauté sûre</p>
                </div>
              </div>
              <button
                onClick={handleGoBack}
                className="flex items-center text-red-100 hover:text-white transition-colors"
              >
                <FaArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </button>
            </div>
          </div>

          {/* Content Preview */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Contenu signalé</h3>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-gray-700 text-sm leading-relaxed">
                {contentPreview}
              </p>
            </div>
          </div>

          {/* Report Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Reason Selection */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                Pourquoi signalez-vous ce contenu ?
              </label>
              <div className="space-y-3">
                {Object.entries(reasonLabels).map(([value, label]) => {
                  const reason = value as ReportReason;
                  return (
                    <label
                      key={reason}
                      className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        selectedReason === reason
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={reason}
                        checked={selectedReason === reason}
                        onChange={() => setSelectedReason(reason)}
                        className="w-4 h-4 text-red-500 bg-white border-gray-300 focus:ring-red-500 focus:ring-2 mt-1"
                      />
                      <div className="ml-3">
                        <div className="font-medium text-gray-900">{label}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {reasonDescriptions[reason]}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Détails supplémentaires (optionnel)
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Décrivez le problème en détail pour nous aider à mieux comprendre votre signalement..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={4}
                maxLength={500}
              />
              <p className="text-sm text-gray-500 mt-2">{details.length}/500 caractères</p>
            </div>

            {/* Community Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FaExclamationTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-2">Règles de la communauté</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    En signalant ce contenu, vous aidez à maintenir une communauté respectueuse et sûre pour tous.
                  </p>
                  <Link
                    to="/community-guidelines"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline font-medium"
                  >
                    Consulter les règles complètes →
                  </Link>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleGoBack}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!selectedReason || submitting}
                className="px-8 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin mr-2" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <FaFlag className="w-4 h-4 mr-2" />
                    Envoyer le signalement
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}